import { useEffect, useState } from "react";
import lodash from "lodash";

// Redux/Hooks/Utils/APIs
import { useFormContext } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { IValuatorRegisterForm, useValuatorRegisterContext, ValuatorRegisterProvider } from "./valuatorRegisterContext";
import { InsurerByValuatorResponse } from "@/main/models/response";
import { valuatorApi } from "@/main/api";
import { ParameterTypesResponse } from "@/main/models/response/parameterTypesResponse";
import { IAddressInfoState } from "@/main/components/shared/addressInfo";
import { formattedContactGet, IContactInfoState } from "@/main/components/shared/contactInfo";
import { getTranslateByKey, isShowControlByElementId } from "@/main/services/utils";
import useKogControlElementId from "@/main/hooks/useKogControlElementId";

// Components
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import TabValuatorClaimSettings from "./tabValuatorClaimSettings/tabValuatorClaimSettings";
import TabValuatorRegister from "./tabValuatorRegister";
import ContainerScreen from "@/main/components/shared/containerScreen/containerLayout";
import { KogTabsComponent } from "@/main/components/shared/tabs/tabs";
import { Controls } from "@/main/services/constants/constantsControl";
import { useUserReducer } from "@/main/store/user";
import { CompanyTypeEnum } from "@/main/services/enum/CompanyTypeEnum";
import LayoutErrorScreen from "@/main/components/layouts/layoutErrorScreen/layoutErrorScreen";
import { RoutesConstants } from "@/main/routes/routesConstants";
import CompanyHistorical from "@/main/components/shared/companyHistorical/companyHistorical";

function ValuatorRegisterScreen() {
	const [dataParameterTypes, setDataParameterTypes] = useState<ParameterTypesResponse[]>([]);
	const [dataContacts, setDataContacts] = useState<IContactInfoState[]>([]);
	const [initialDataContacts, setInitialDataContacts] = useState<IContactInfoState[]>([]);
	const [dataAddress, setDataAddress] = useState<IAddressInfoState[]>([]);
	const [initialDataAddress, setInitialDataAddress] = useState<IAddressInfoState[]>([]);
	const [dataInsurers, setDataInsurers] = useState<InsurerByValuatorResponse[]>([]);
	const [isLoadingInfo, setIsLoadingInfo] = useState<boolean>(false);
	const [actAsSupervisor, SetActAsSupervisor] = useState<boolean>(false);

	const [guidCompany, setGuidCompany] = useState<string>("");
	const [isBlockedScreen, setBlockedScreen] = useState<boolean>(false);

	const [searchParams] = useSearchParams();
	const guidValuatorParams = searchParams.get("valuatorGuid");

	const showTabSettings = useKogControlElementId(Controls.IdTabSettings);

	const methods = useFormContext<IValuatorRegisterForm>();

	const { isEdit, setIsEdit } = useValuatorRegisterContext();

	const userReducer = useUserReducer();

	const guidInsurer =
		userReducer.currentUser?.idCompanyType == CompanyTypeEnum.Insurer ? userReducer.currentUser?.guidCompany : null;

	function loadInfoValuator(guid: string, guidInsurer: string) {
		setIsLoadingInfo(true);
		valuatorApi
			.getValuatorData({ guid: guid, guidInsurerLog: guidInsurer })
			.then((response) => {
				const { result } = response.data;
				methods.reset({
					country: {
						key: result.country.guid,
						value: result.country.name,
					},
					name: result.name,
					rfc: result.registrationNumber,
					statusRegister: {
						key: result.statusRegister.idStatusRegister,
						value: result.statusRegister.name,
					},
				});

				const formattedContacts = result.contacts?.map((contact) => formattedContactGet(contact));

				const formattedAddress = result.addresses?.map((address) => ({
					id: address.guid,
					address: address.street,
					complement: address.complement,
					number: address.number === "" ? null : address.number,
					zipCode: address.municipality.zipCode,
					addressType: {
						key: address.addressType.idAddressType,
						value: address.addressType.name,
					},
					city: {
						key: address.municipality.city.guid,
						value: address.municipality.city.name,
					},
					municipality: {
						key: address.municipality.guid,
						value: address.municipality.name,
					},
					state: {
						key: address.municipality.city.state.guid,
						value: address.municipality.city.state.name,
					},
				})) as IAddressInfoState[];

				setGuidCompany(result.guidCompany);
				const contacts = formattedContacts ?? [];
				setDataContacts(lodash.cloneDeep(contacts));
				setInitialDataContacts(lodash.cloneDeep(contacts));
				const addresses = formattedAddress ?? [];
				setDataAddress(lodash.cloneDeep(addresses));
				setInitialDataAddress(lodash.cloneDeep(addresses));
				SetActAsSupervisor(result.actAsSupervisor);
				setDataParameterTypes(result.parameterTypes);
				setBlockedScreen(result.hasOtherCompanyRelation);
			})
			.catch((e) => {
				console.error(e);
			})
			.finally(() => {
				setIsLoadingInfo(false);
			});
	}

	function loadInsurersByValuator(guid: string, guidInsurer: string) {
		valuatorApi
			.getAllInsurerByValuator(guid, guidInsurer)
			.then((response) => {
				const { result } = response.data;
				setDataInsurers(result);
			})
			.catch(() => {});
	}

	useEffect(() => {
		if (guidValuatorParams) {
			setIsEdit(true);
			loadInfoValuator(guidValuatorParams, guidInsurer ?? "");
			loadInsurersByValuator(guidValuatorParams, guidInsurer ?? "");
		}
	}, [guidValuatorParams]);

	if (isLoadingInfo) {
		return <LoadingComponent />;
	}

	if (isBlockedScreen) {
		return (
			<LayoutErrorScreen
				message={getTranslateByKey("information.valuatorAssociatedWithAnotherInsurance", true)}
				returnTo={RoutesConstants.registrationSearchValuator}
			/>
		);
	}

	const INTIAL_TAB_GENERAL_INFO: number = 0;
	const tabPanels = [
		{
			tab: getTranslateByKey("control.tabGeneralInfo"),
			panel: (
				<TabValuatorRegister
					dataContacts={dataContacts}
					initialDataContacts={initialDataContacts}
					dataAddress={dataAddress}
					initialDataAddress={initialDataAddress}
					dataInsurers={dataInsurers}
					setDataAddress={setDataAddress}
					setDataContacts={setDataContacts}
					loadInsurersByValuator={loadInsurersByValuator}
					loadInfoValuator={loadInfoValuator}
				/>
			),
			isShowControl: true,
		},
		{
			tab: getTranslateByKey("control.tabSettings"),
			panel: (
				<TabValuatorClaimSettings
					parameterTypes={dataParameterTypes}
					setDataParameterTypes={setDataParameterTypes}
					guidCompany={guidCompany}
					SetActAsSupervisor={SetActAsSupervisor}
					actAsSupervisor={actAsSupervisor}
					guidValuator={guidValuatorParams}
				/>
			),
			isShowControl: isEdit && showTabSettings,
		},
		{
			tab: getTranslateByKey("control.historical"),
			panel: <CompanyHistorical guidCompany={guidCompany} />,
			isShowControl: isEdit && isShowControlByElementId(Controls.IdCompanyHistoricalValuator),
		},
	];

	return (
		<ContainerScreen
			title={`${getTranslateByKey("information.registration")} > ${getTranslateByKey("screen.valuatorRegistration")}`}
		>
			<KogTabsComponent tabPanels={tabPanels} tabCurrent={INTIAL_TAB_GENERAL_INFO} onChangeTab={() => {}} />
		</ContainerScreen>
	);
}

export default function ValuatorRegisterScreenLoaded() {
	return (
		<ValuatorRegisterProvider>
			<ValuatorRegisterScreen />
		</ValuatorRegisterProvider>
	);
}
